// helpers for showing S3 files WITHOUT signed download URLs (those expire after an hour)
// - getS3FileDownloadRedirectUrl:  link to our redirect view, which signs a fresh URL when the user clicks
// - loadS3FileMeta:  get filename, size, type, etc. for a file - BATCHED, so 100 buttons on a page = 1 request

import { suppliosApi } from '../../js/api-service';

const BATCH_WAIT_MS = 10; // collect calls from all components mounting at the same time
const MAX_BATCH_SIZE = 100; // backend allows 250

const metaCache = new Map(); // key -> Promise of file meta - safe to keep for the page lifetime, metadata never changes for a file ID
let pendingByKey = new Map(); // key -> { key, category, id, resolve, reject }
let flushTimer = null;

export function getS3FileMetaKey(category, id) {
  // same format as get_s3_file_meta_key() in core_files/services/file_meta.py - keep them in sync
  return `${category}:${id}`;
}

export function getS3FileDownloadRedirectUrl(category, id) {
  // matches 's3_file_download_redirect' in core_files/urls.py - keep them in sync
  return `/core-files/download/${encodeURIComponent(category)}/${encodeURIComponent(id)}/`;
}

async function fetchBatch(batch) {
  try {
    const { data } = await suppliosApi.files.getS3FilesMeta(batch.map(({ category, id }) => ({ category, id })));
    const metaByKey = new Map((data?.files ?? []).map((fileMeta) => [getS3FileMetaKey(fileMeta.file_category, fileMeta.id), fileMeta]));
    batch.forEach((item) => {
      const fileMeta = metaByKey.get(item.key) ?? null;
      if (!fileMeta) {
        metaCache.delete(item.key); // don't cache 'not found' -- let a later mount try again
      }
      item.resolve(fileMeta);
    });
  } catch (err) {
    batch.forEach((item) => {
      metaCache.delete(item.key);
      item.reject(err);
    });
  }
}

function flushPending() {
  flushTimer = null;
  const items = [...pendingByKey.values()];
  pendingByKey = new Map();
  for (let i = 0; i < items.length; i += MAX_BATCH_SIZE) {
    fetchBatch(items.slice(i, i + MAX_BATCH_SIZE));
  }
}

export function loadS3FileMeta(category, id) {
  // resolves to file meta {id, file_category, can_read, filename, filesize, filetype, file_extension, title, name}
  // or null if the file was not found - rejects if the request failed
  const key = getS3FileMetaKey(category, id);
  if (metaCache.has(key)) {
    // console.log(`Cache hit for key: ${key}`);
    return metaCache.get(key);
  }
  const metaPromise = new Promise((resolve, reject) => {
    pendingByKey.set(key, { key, category, id, resolve, reject });
    // console.log(`Added pending request for key: ${key}`);
  });
  // console.log(`Meta promise created for key: ${key}`);
  metaCache.set(key, metaPromise);
  if (!flushTimer) {
    flushTimer = setTimeout(flushPending, BATCH_WAIT_MS);
  }
  return metaPromise;
}
